import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/shadcn/collapsible'
import { cn } from '@/lib/utils'
import { ChevronDown } from 'lucide-react'
import { useRef, useState } from 'react'

const COLLAPSIBLE_EXPAND_MS = 220

function getScrollParent(element: HTMLElement | null): HTMLElement | null {
  let parent = element?.parentElement ?? null

  while (parent) {
    const { overflowY } = window.getComputedStyle(parent)
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent
    }
    parent = parent.parentElement
  }

  return null
}

function scrollSectionHeaderToTop(section: HTMLElement) {
  const scrollContainer = getScrollParent(section)
  if (!scrollContainer) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }

  const containerRect = scrollContainer.getBoundingClientRect()
  const sectionRect = section.getBoundingClientRect()
  const targetScrollTop = scrollContainer.scrollTop + (sectionRect.top - containerRect.top)

  scrollContainer.scrollTo({ top: targetScrollTop, behavior: 'smooth' })
}

export function DesignSystemSection({
  title,
  description,
  defaultOpen = false,
  contentClassName,
  children,
}: {
  title: string
  description?: string
  defaultOpen?: boolean
  contentClassName?: string
  children: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const sectionRef = useRef<HTMLDivElement>(null)
  const shouldScrollRef = useRef(false)
  const scrollTimeoutRef = useRef<number>()

  const scrollAfterExpand = () => {
    if (!shouldScrollRef.current || !sectionRef.current) {
      return
    }

    scrollSectionHeaderToTop(sectionRef.current)
  }

  const scheduleScrollAfterExpand = () => {
    window.clearTimeout(scrollTimeoutRef.current)
    scrollTimeoutRef.current = window.setTimeout(scrollAfterExpand, COLLAPSIBLE_EXPAND_MS)
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    shouldScrollRef.current = open

    if (!open) {
      window.clearTimeout(scrollTimeoutRef.current)
      return
    }

    scheduleScrollAfterExpand()
  }

  const handleContentAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (event.animationName !== 'collapsible-down') {
      return
    }

    window.clearTimeout(scrollTimeoutRef.current)
    scrollAfterExpand()
  }

  return (
    <div ref={sectionRef}>
      <Collapsible open={isOpen} onOpenChange={handleOpenChange}>
        <CollapsibleTrigger className="flex w-full items-center gap-4 bg-muted px-5 py-4 text-left hover:bg-foreground/5">
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-foreground">{title}</p>
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
          <ChevronDown
            className={cn('size-5 shrink-0 text-muted-foreground transition-transform', isOpen && 'rotate-180')}
          />
        </CollapsibleTrigger>
        <CollapsibleContent
          className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down"
          onAnimationEnd={handleContentAnimationEnd}
        >
          <div className={cn('border-t bg-offwhite p-6', contentClassName)}>{children}</div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}
